/**
 * Artikelsuche fuer die Belegerfassung.
 *
 * Ein Eingabefeld mit Vorschlagsliste statt eines Auswahlfeldes: Bei
 * einigen hundert Artikeln ist eine Klappliste unbrauchbar, und in der
 * Praxis wird ohnehin die Artikelnummer getippt oder ein Barcode
 * eingescannt.
 *
 * Die Vorschlagsliste haengt per Portal am Dokument, nicht am Eingabefeld:
 * In der Positionstabelle schneidet der scrollbare Rahmen (`.table-wrap`)
 * sonst alles ab, was ueber die Tabelle hinausragt - bei einer einzigen
 * Positionszeile war die Liste dadurch unsichtbar.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';

import { formatPrice, formatQuantity, variantAttributesText } from '@erp/shared';

import { api, withQuery } from '../api/client';
import type { Item, ListResponse } from '../api/types';

type Anchor = { top: number; bottom: number; left: number; width: number };

export function ItemPicker({
  value,
  onSelect,
  disabled,
  placeholder = 'Artikelnummer oder Bezeichnung …',
}: {
  /** Angezeigter Text, wenn kein Vorschlag offen ist. */
  value: string;
  onSelect: (item: Item) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['item-search', search],
    queryFn: () =>
      // Elternartikel sind nur die Klammer einer Variantenfamilie und kommen
      // nicht in den Beleg - der Anwender waehlt die konkrete Variante.
      api.get<ListResponse<Item>>(withQuery('/api/items', { q: search, limit: 8, active: 'true', kind: 'selectable' })),
    enabled: open && search.trim().length > 0,
  });

  const results = data?.items ?? [];

  function updateAnchor() {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) setAnchor({ top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width });
  }

  // Klick ausserhalb schliesst die Vorschlagsliste; beim Rollen oder
  // Vergroessern des Fensters wandert sie mit dem Eingabefeld mit.
  useEffect(() => {
    if (!open) return;

    function handleClick(event: MouseEvent) {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', updateAnchor, true);
    window.addEventListener('resize', updateAnchor);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', updateAnchor, true);
      window.removeEventListener('resize', updateAnchor);
    };
  }, [open]);

  function choose(item: Item) {
    onSelect(item);
    setSearch('');
    setOpen(false);
  }

  // Unterhalb des Feldes oeffnen; reicht der Platz bis zum unteren
  // Fensterrand nicht, stattdessen darueber.
  const maxHeight = 280;
  const openUp =
    anchor !== null &&
    anchor.bottom + maxHeight + 4 > window.innerHeight &&
    anchor.top > maxHeight + 4;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        // "off" ignoriert Chrome bei Feldern, die es für Adressfelder hält, und
        // bietet gespeicherte Adressen an. Ein anderes Token unterdrückt das.
        autoComplete="one-time-code"
        className="input"
        value={open ? search : value}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={() => {
          setSearch('');
          setOpen(true);
          updateAnchor();
        }}
        onChange={(event) => {
          setSearch(event.target.value);
          setHighlighted(0);
          setOpen(true);
          updateAnchor();
        }}
        onKeyDown={(event) => {
          if (!open || results.length === 0) return;

          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlighted((current) => Math.min(current + 1, results.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted((current) => Math.max(current - 1, 0));
          } else if (event.key === 'Enter') {
            event.preventDefault();
            const item = results[highlighted];
            if (item) choose(item);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />

      {open && anchor && results.length > 0
        ? createPortal(
            <div
              ref={listRef}
              style={{
                position: 'fixed',
                zIndex: 40,
                ...(openUp
                  ? { bottom: window.innerHeight - anchor.top + 2 }
                  : { top: anchor.bottom + 2 }),
                left: anchor.left,
                width: Math.max(anchor.width, 380),
                background: 'var(--bg-raised)',
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-sm)',
                boxShadow: 'var(--shadow-lg)',
                maxHeight,
                overflowY: 'auto',
              }}
            >
              {results.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    choose(item);
                  }}
                  onMouseEnter={() => setHighlighted(index)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '6px 10px',
                    border: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    background: index === highlighted ? 'var(--bg-hover)' : 'transparent',
                    color: 'inherit',
                    font: 'inherit',
                  }}
                >
                  <div className="row-between">
                    <span>
                      <span className="mono faint">{item.number}</span> {item.name}
                      {item.kind === 'set' ? <span className="faint"> · Set</span> : null}
                      {variantAttributesText(item.variantAttributes) ? (
                        <span className="faint"> · {variantAttributesText(item.variantAttributes)}</span>
                      ) : null}
                    </span>
                    <span className="mono" style={{ fontSize: 12.5 }}>
                      {formatPrice(item.salesPrice)} €
                    </span>
                  </div>
                  {item.isStockItem ? (
                    <div className="faint" style={{ fontSize: 11.5 }}>
                      Bestand {formatQuantity(item.stockQuantity)}
                    </div>
                  ) : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
